import React, { useState, useEffect } from 'react';
import therapeuticAreaConfig from '../../config/therapeuticAreaConfig.json';
import StudyIcons from '../StudyIcons/StudyIcons';
import './LivePreview.css';

// Helper function to check if HTML content is empty (just tags like <p><br></p> or <p></p>)
const isHtmlEmpty = (html) => {
  if (!html || html === '') return true;
  const stripped = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return stripped.length === 0;
};

// Helper function to convert image URL to base64
const convertImageToBase64 = async (url) => {
  try {
    // Check if it's already a base64 string
    if (url.startsWith('data:image')) {
      return url;
    }

    // Fetch the image from the URL
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit'
    });

    if (!response.ok) {
      console.error('Failed to fetch image:', url);
      return url; // Return original URL if fetch fails
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return url; // Return original URL on error
  }
};

// Header background images mapping
const headerBackgroundImages = {
  1: '/images/headers/GNE_Ophthalmology_Topic_Header.png',
  2: '/images/headers/GNE_Mouse_Eye_Blue_Green.png',
  3: '/images/headers/GNE_Retina_Blue.png',
};

// Component that renders SVG icons with dynamic colors based on study type
const StudyIcon = ({ iconKey, color }) => {
  if (!iconKey || iconKey === 'none') {
    const NoneIcon = StudyIcons.none;
    return <NoneIcon color={color || '#7F7F7F'} />;
  }

  const IconComponent = StudyIcons[iconKey];
  if (!IconComponent) return null;

  return <IconComponent color={color} />;
};
// Helper function to check if HTML content has actual text (not just empty tags)
const hasContent = (html) => {
  if (!html) return false;
  // Strip HTML tags, decode HTML entities, and check if there's any text content
  const textContent = html
    .replace(/<[^>]*>/g, '')  // Remove HTML tags
    .replace(/&nbsp;/g, ' ')   // Replace non-breaking spaces
    .replace(/&amp;/g, '&')    // Decode ampersand
    .replace(/&lt;/g, '<')     // Decode less than
    .replace(/&gt;/g, '>')     // Decode greater than
    .trim();
  return textContent.length > 0;
};

const getFirstPText = (html = '') => {
  if (!html) return '';

  // grab first <p>...</p> if present, else use entire string
  const match = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  const firstChunk = match ? match[1] : html;

  return firstChunk
    .replace(/<[^>]*>/g, '')   // strip tags
    .replace(/&nbsp;/g, ' ')   // normalize spaces
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
};

const truncateWithEllipsis = (text = '', maxChars = 20) => {
  if (!text) return '';
  return text.length > maxChars ? text.slice(0, maxChars) + '…' : text;
};

// Truncate author name with "et al." suffix when exceeding character limit
// If truncated mid-word, adds ellipsis; if ends at natural break (comma/space), no ellipsis
const truncateAuthorWithEtAl = (text = '', maxChars = 25) => {
  if (!text) return '';
  if (text.length > maxChars) {
    const truncated = text.slice(0, maxChars).trim();
    const lastChar = truncated.slice(-1);
    // Check if ends at a natural break (comma or if next char in original is space/comma)
    const nextCharInOriginal = text.charAt(maxChars);
    const endsCleanly = lastChar === ',' || nextCharInOriginal === ' ' || nextCharInOriginal === ',';

    if (endsCleanly) {
      // Clean break - no ellipsis needed
      const cleanTruncated = truncated.endsWith(',') ? truncated.slice(0, -1).trim() : truncated;
      return cleanTruncated + ', et al.';
    } else {
      // Mid-word truncation - add ellipsis
      return truncated + '..., et al.';
    }
  }
  return text;
};

// Helper function to add pdf-external-link class to all anchor tags in HTML content
const addPdfLinkClass = (html) => {
  if (!html) return '';
  // Add pdf-external-link class and target="_blank" to all <a> tags that don't already have the class
  return html.replace(/<a\s+([^>]*href=[^>]*)>/gi, (match, attributes) => {
    // Check if already has pdf-external-link class
    if (attributes.includes('pdf-external-link')) {
      return match;
    }
    // Check if already has a class attribute
    if (attributes.includes('class=')) {
      return match.replace(/class="([^"]*)"/, 'class="$1 pdf-external-link"')
                  .replace(/class='([^']*)'/, "class='$1 pdf-external-link'");
    }
    // Add class and target attributes
    return `<a class="pdf-external-link" target="_blank" rel="noopener noreferrer" ${attributes}>`;
  });
};

// Helper function to convert newlines to <br> tags for display
const convertNewlinesToBr = (text) => {
  if (!text) return '';
  // Escape HTML entities first, then convert newlines to <br>
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
};

const LivePreview = ({ guide, theme, currentPage = 0, onPageChange, isExporting = false, onAddStudy, onGuideUpdate }) => {
  // Ensure studies array exists
  const studies = guide?.studies || [];

  // State to store base64 images for each study
  const [studyImagesBase64, setStudyImagesBase64] = useState({});

  // Convert all study images to base64 when guide changes
  useEffect(() => {
    const loadImages = async () => {
      if (!guide?.studies || guide.studies.length === 0) {
        return;
      }

      const imagePromises = guide.studies.map(async (study) => {
        if (study.image && !study.image.startsWith('data:image')) {
          const base64 = await convertImageToBase64(study.image);
          return { id: study.id, base64 };
        }
        return { id: study.id, base64: study.image };
      });

      const loadedImages = await Promise.all(imagePromises);
      const imagesMap = {};
      loadedImages.forEach(({ id, base64 }) => {
        if (base64) {
          imagesMap[id] = base64;
        }
      });
      setStudyImagesBase64(imagesMap);
    };

    loadImages();
  }, [guide]);

  // Page 0 = Home page, Page 1+ = Study pages
  const isHomePage = currentPage === 0;
  const totalPages = studies.length + 1; // +1 for home page

  const getStudyColor = (type) => {
    switch (type) {
      case 'LTE':
        return {
          primary: '#0366D3',
          secondary: '#0366D3',
          light: '#DBEAFE',
          accent: '#0366D3',
          badge: '#0366D3',
        };
      case 'RWE':
        return {
          primary: '#DB4505',
          secondary: '#DB4505',
          light: '#FCE7F3',
          accent: '#DB4505',
          badge: '#DB4505',
        };
      case 'BM':
        return {
          primary: '#12284A',
          secondary: '#12284A',
          light: '#F3F4F6',
          accent: '#12284A',
          badge: '#12284A',
        };
      case 'Ph2':
        return {
          primary: '#AE2185',
          secondary: '#AE2185',
          light: '#FCE7F3',
          accent: '#AE2185',
          badge: '#AE2185',
        };
      case 'Ph3':
        return {
          primary: '#460968',
          secondary: '#460968',
          light: '#F3F4F6',
          accent: '#460968',
          badge: '#460968',
        };
      default:
        return {
          primary: '#0366D3',
          secondary: '#0366D3',
          light: '#DBEAFE',
          accent: '#0366D3',
          badge: '#0366D3',
        };
    }
  };

  const getHeaderBackgroundImage = () => {
    // Get header background from therapeutic area config if available
    const therapeuticArea = guide.therapeutic_area;
    if (therapeuticArea && therapeuticAreaConfig[therapeuticArea]) {
      const backgrounds = therapeuticAreaConfig[therapeuticArea].headerBackgrounds;
      const selectedBackground = backgrounds.find(bg => bg.id === guide.header_option);
      if (selectedBackground) {
        return selectedBackground.image;
      }
      // Fallback to first background if selected option doesn't exist
      if (backgrounds.length > 0) {
        return backgrounds[0].image;
      }
    }
    // Fallback to default mapping
    return headerBackgroundImages[guide.header_option] || headerBackgroundImages[1];
  };

  const handleTocClick = (index) => {
    if (onPageChange) {
      onPageChange(index + 1); // +1 because page 0 is home page
    }
  };

  const handleHomeClick = () => {
    if (onPageChange) {
      onPageChange(0); // Go to home page
    }
  };

  const handleStudyCardClick = (studyIndex) => {
    if (onPageChange) {
      onPageChange(studyIndex + 1); // +1 because page 0 is home page
    }
  };

  // Get study type full name
  const getStudyTypeName = (type) => {
    switch (type) {
      case 'LTE': return 'Long-term Extension';
      case 'RWE': return 'Real World Experience';
      case 'BM': return 'Biomarker';
      case 'Ph2': return 'Phase 2';
      case 'Ph3': return 'Phase 3';
      default: return type;
    }
  };

  const stripOuterPTags = (html = '') =>
  html
    .replace(/^<p>/i, '')
    .replace(/<\/p>$/i, '')
    .replace(/<p><br><\/p>/gi, '');

  const sanitizeRichTextForInline = (html = '') =>
    addPdfLinkClass(stripOuterPTags(html));

  // Render page header (used on all pages)
  const renderPageHeader = () => (
    <div
      className="page-header-banner"
      style={{ backgroundImage: `url(${getHeaderBackgroundImage()})` }}
    >
      <div className="page-header-content">
        <div className="page-header-text">
          {hasContent(guide.title) && (
            <h1
              className="page-header-title"
              dangerouslySetInnerHTML={{
                __html: addPdfLinkClass(stripOuterPTags(guide.title)),
              }}
            />
          )}

          {hasContent(guide.event_location) && (
            <div
              className="page-header-subtitle"
              dangerouslySetInnerHTML={{
                __html: addPdfLinkClass(stripOuterPTags(guide.event_location)),
              }}
            />
          )}
        </div>
        {/* <div className="page-header-logo">
          <img src="/genentech-logo.svg" />
        </div> */}
      </div>
    </div>
  );

  // Render TOC navigation bar (used on all pages)
  const renderTocBar = (activeStudyIndex = -1) => {
    if (studies.length === 0) return null;
    return (
      <div className="cover-toc-bar">
        <button
          className={`toc-home-btn pdf-nav-item ${activeStudyIndex === -1 ? 'active' : ''}`}
          data-page-index="0"
          onClick={handleHomeClick}
          title="Go to Home"
        >
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.1159 11.8543V7.13713C14.1159 6.6828 14.1155 6.45551 14.0603 6.24411C14.0113 6.05676 13.9309 5.87946 13.8222 5.71925C13.6995 5.53846 13.5288 5.38854 13.1869 5.08936L9.10566 1.51827C8.47085 0.962806 8.15344 0.685216 7.79622 0.579578C7.48147 0.486495 7.14597 0.486495 6.83121 0.579578C6.47427 0.685136 6.15732 0.962464 5.52346 1.51709L1.44086 5.08936C1.09894 5.38854 0.928381 5.53846 0.805656 5.71925C0.696899 5.87946 0.615917 6.05676 0.566961 6.24411C0.511719 6.45552 0.511719 6.6828 0.511719 7.13713V11.8543C0.511719 12.6467 0.511719 13.0427 0.641163 13.3552C0.813756 13.7719 1.14458 14.1033 1.56126 14.2759C1.87377 14.4054 2.26994 14.4054 3.06228 14.4054C3.85462 14.4054 4.25123 14.4054 4.56374 14.2759C4.98042 14.1033 5.31116 13.7719 5.48375 13.3553C5.61319 13.0428 5.61328 12.6466 5.61328 11.8542V11.004C5.61328 10.0648 6.37463 9.30347 7.3138 9.30347C8.25297 9.30347 9.01432 10.0648 9.01432 11.004V11.8542C9.01432 12.6466 9.01432 13.0428 9.14377 13.3553C9.31636 13.7719 9.64719 14.1033 10.0639 14.2759C10.3764 14.4054 10.7725 14.4054 11.5649 14.4054C12.3572 14.4054 12.7538 14.4054 13.0663 14.2759C13.483 14.1033 13.8138 13.7719 13.9864 13.3552C14.1158 13.0427 14.1159 12.6467 14.1159 11.8543Z" stroke="white" strokeWidth="1.02031" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        {studies.map((study, index) => {
          const raw = study.name || study.title || '';
          const label = truncateWithEllipsis(getFirstPText(raw), 20);

          return (
            <button
              key={study.id}
              className={`cover-toc-item pdf-nav-item ${activeStudyIndex === index ? 'active' : ''}`}
              data-page-index={index + 1}
              onClick={() => handleTocClick(index)}
              title={getFirstPText(raw)} // full text on hover
            >
              <span className="toc-item-text">{label || `Study ${index + 1}`}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Render page footer (used on all pages)
  // hidePagination: optional flag to hide the pagination controls (used on home page)
  const renderPageFooter = (pageNumber, pageTotalPages, hidePagination = false) => (
    <>
      <div className="page-footer">
        <div className={`page-footer-top ${guide.show_society_links === false && guide.show_genentech_access === false ? 'center-content' : ''}`}>
          {guide.show_society_links !== false && (
            <div className="footer-societies">
              <span className="footer-label" dangerouslySetInnerHTML={{ __html: isHtmlEmpty(guide.society_label_text) ? 'Click to visit these Societies:' : guide.society_label_text }} />
              {guide.society1_name?.trim() && guide.society1_url?.trim() && (
                <a href={guide.society1_url.trim()} className="footer-society-btn pdf-external-link" target="_blank" rel="noopener noreferrer" title={guide.society1_name.trim()}>
                  <span className="btn-text">{guide.society1_name.trim()}</span>
                </a>
              )}
              {guide.society2_name?.trim() && guide.society2_url?.trim() && (
                <a href={guide.society2_url.trim()} className="footer-society-btn pdf-external-link" target="_blank" rel="noopener noreferrer" title={guide.society2_name.trim()}>
                  <span className="btn-text">{guide.society2_name.trim()}</span>
                </a>
              )}
            </div>
          )}
          {guide.include_medically && (
            <div className="footer-medically-badge">
              {guide.show_genentech_access !== false && guide.include_medically && (
                <div className="footer-genentech-access">
                  <span className="footer-access-text" dangerouslySetInnerHTML={{ __html: isHtmlEmpty(guide.genentech_access_text) ? 'Access Genentech sponsored presentations' : guide.genentech_access_text }} />
                </div>
              )}
              <a href={guide.medically_url || 'https://medically.gene.com/'} className="pdf-external-link" target="_blank" rel="noopener noreferrer">
                <img src="/medically.svg" alt="Medically" />
              </a>
            </div>
          )}
        </div>
        <div className="page-footer-bottom">
          <div className="footer-genentech-brand">
            <img
              src='/genentech-logo-blue.svg'
              alt="Genentech"
            />
          </div>
          <span className="footer-copyright">
            {guide.project_code ? ` ${guide.project_code}` : ''}
          </span>
        </div>
      </div>
      {!isExporting && !hidePagination && pageTotalPages > 1 && (
        <div className="page-navigation-footer">
          <button
            className="page-nav-btn prev"
            onClick={() => onPageChange && onPageChange(pageNumber - 2)}
            disabled={pageNumber <= 1}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="page-nav-indicator">Page {pageNumber} of {pageTotalPages}</span>
          <button
            className="page-nav-btn next"
            onClick={() => onPageChange && onPageChange(pageNumber)}
            disabled={pageNumber >= pageTotalPages}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M8 15L13 10L8 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </>
  );

  // Render Home/Cover page with study cards grid
  const renderHomePage = () => {
    const isVisible = currentPage === 0;
    return (
      <div
        id="preview-home-page"
        className={`preview-page home-page ${isVisible ? 'visible' : 'hidden-for-slider'}`}
      >
        {renderPageHeader()}
        {/* No TOC bar on home page */}

        {/* Disclaimers Section */}
        {hasContent(guide.disclaimers) && (
          <div
            className="study-disclaimers"
            dangerouslySetInnerHTML={{
              __html: addPdfLinkClass(stripOuterPTags(guide.disclaimers)),
            }}
          />
        )}

        {/* Home Page Content */}
        <div className="home-page-content">
          {/* Study Cards Grid */}
          <div className="home-studies-grid">
            {studies.map((study, index) => {
              const colors = getStudyColor(study.type);
              // No background image on cards - white cards in both modes
              const cardStyle = { '--study-primary': colors.primary };
              return (
                <div
                  key={study.id}
                  className="home-study-card pdf-nav-item"
                  onClick={() => handleStudyCardClick(index)}
                  style={cardStyle}
                  data-page-index={index + 1}
                >
                  {/* Badge at top-right corner */}
                  {study.type && (
                    <div
                      className="home-study-type-badge"
                      style={{ backgroundColor: colors.badge }}
                    >
                      {study.type}
                    </div>
                  )}
                  {/* Icon on the left */}
                  <div className="home-study-icon">
                    {study.icon && study.icon !== 'none' && <StudyIcon iconKey={study.icon} color={colors.primary} />}
                  </div>
                  {/* Content on the right */}
                  <div className="home-study-card-content">
                    <div className="home-study-card-content-inner">
                      {study.name && (
                        <h3
                          className="home-study-name"
                          style={{ color: colors.primary }}
                          dangerouslySetInnerHTML={{ __html: sanitizeRichTextForInline(study.name) }}
                        />
                      )}
                      {study.title && (
                        <p
                          className="home-study-title"
                          style={{ color: colors.primary }}
                          dangerouslySetInnerHTML={{ __html: sanitizeRichTextForInline(study.title) }}
                        />
                      )}
                      {study.author_name && (
                        <p className="home-study-author">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: `— ${truncateAuthorWithEtAl(sanitizeRichTextForInline(study.author_name) + (study.degree ? `, ${study.degree}` : ''), 22)}`,
                          }}
                        />
                      </p>
                      )}
                    </div>

                    <div className="home-study-affiliated-authors-wrapper">
                      {/* Affiliated Authors on Home Card - show when toggle is enabled and has authors */}
                      {study.show_affiliated_authors !== false && (study.author_1_name?.trim() || study.author_2_name?.trim()) && (
                        <div className="home-study-affiliated-authors">
                          <p className="affiliated-authors-title">
                            {study.affiliated_authors_title || 'Retina Consultants of America (RCA) affiliated author:'}
                          </p>
                          <div className="affiliated-authors-content">
                            <div className="affiliated-author-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.2599 18.5381L10.4647 18.9068C10.8744 18.7634 11.1816 18.3537 11.0382 18.026L10.055 15.9981C9.74776 15.3836 9.09229 15.0558 8.25246 15.3631C7.43311 15.6499 7.12586 16.3258 7.24876 17.0018L7.76085 19.1935C7.86327 19.5418 8.37536 19.6647 8.76455 19.5213L8.62116 19.1321" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M14.0078 16.734C14.0078 17.3895 14.5404 17.9015 15.1754 17.9015C15.8104 17.9015 16.3429 17.369 16.3429 16.734C16.3429 16.099 15.8104 15.5664 15.1754 15.5664C14.5404 15.5664 14.0078 16.099 14.0078 16.734Z" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M4.42188 20.1564L4.72913 16.0597C4.87252 14.7078 7.00281 13.4173 9.54279 12.8438L11.3863 14.7487C11.7345 15.1174 12.2671 15.1174 12.6153 14.7487L14.4589 12.8438C16.9783 13.4173 19.1086 14.6873 19.2725 16.0597L19.5798 20.1564" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M15.5234 15.6097C15.8921 14.7289 16.0355 14.1963 16.0765 13.418" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M7.80078 13.457C7.82126 14.174 7.96465 14.7885 8.23094 15.362" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M12.1428 11.6138C14.3036 11.6138 16.0552 9.86218 16.0552 7.70144C16.0552 5.54069 14.3036 3.78906 12.1428 3.78906C9.9821 3.78906 8.23047 5.54069 8.23047 7.70144C8.23047 9.86218 9.9821 11.6138 12.1428 11.6138Z" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                              <circle cx="12" cy="12" r="11.4" stroke="#7F7F7F" stroke-width="1.2"/>
                              </svg>
                            </div>
                            <div className="affiliated-authors-names">
                              {study.author_1_name?.trim() && (
                                <span className="affiliated-author-name">
                                  {study.author_1_name.trim().length > 20
                                    ? truncateAuthorWithEtAl(study.author_1_name.trim(), 20)
                                    : study.author_1_name.trim()}
                                </span>
                              )}
                              {study.author_2_name?.trim() && (
                                <span className="affiliated-author-name">
                                  {study.author_2_name.trim().length > 20
                                    ? truncateAuthorWithEtAl(study.author_2_name.trim(), 20)
                                    : study.author_2_name.trim()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Study Type Legend - appears after all cards */}
          {guide.studies && guide.studies.length > 0 && Array.from(new Set(guide.studies.map(s => s.type).filter(Boolean))).length > 0 && (
            <div className="home-study-type-legends">
              {Array.from(new Set(guide.studies.map(s => s.type).filter(Boolean))).map(type => {
                const colors = getStudyColor(type);
                return (
                  <div key={type} className="home-study-type-legend-item">
                    <span
                      className="legend-dot"
                      style={{ backgroundColor: colors.badge }}
                    ></span>
                    <span className="legend-text">{type}: {getStudyTypeName(type)}</span>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Authors Panel removed from home - now shown in each study page */}

        {/* Spacer to push footer to bottom */}
        {/* <div className="page-content-spacer"></div> */}

        {renderPageFooter(1, totalPages, false)}
      </div>
    );
  };

  // Render all pages - Home page + Study pages
  const renderAllPages = () => {
    return (
      <>
        {/* Home Page */}
        {renderHomePage()}

        {/* Study Pages */}
        {studies.map((study, index) => {
          const colors = getStudyColor(study.type);
          const isVisible = currentPage === index + 1; // +1 because page 0 is home
          return (
            <div
              key={`${study.id}-${study.type}-${study.name}`}
              id={`preview-study-${study.id}`}
              className={`preview-page study-page ${isVisible ? 'visible' : 'hidden-for-slider'}`}
              style={{
                '--study-primary': colors.primary,
                '--study-secondary': colors.secondary,
                '--study-light': colors.light,
                '--study-accent': colors.accent,
              }}
            >
              {renderPageHeader()}
              {renderTocBar(index)}

              {/* Disclaimers Section - shown on each study page */}
              {hasContent(guide.disclaimers) && (
              <div
                className="study-disclaimers"
                dangerouslySetInnerHTML={{
                  __html: addPdfLinkClass(stripOuterPTags(guide.disclaimers)),
                }}
              />
            )}

              {/* Study Header Section */}
              <div className="study-header-section">
                <div className="study-header-left">
                  {study.icon && study.icon !== 'none' && (
                    <div className={`study-header-icon study-header-icon--${(study.type || 'default').toLowerCase()}`}>
                      <StudyIcon iconKey={study.icon} color={colors.primary} />
                    </div>
                  )}
                  <div className="study-header-info">
                    {study.name && (
                      <h2
                        className="study-name"
                        style={{ color: colors.primary }}
                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextForInline(study.name) }}
                      />
                    )}
                    {study.title && (
                      <p
                        className="study-title-text"
                        style={{ color: colors.primary }}
                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextForInline(study.title) }}
                      />
                    )}
                    {study.author_name && (
                      <p className="study-author">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: `— ${sanitizeRichTextForInline(study.author_name)}`,
                          }}
                        />
                      </p>
                    )}
                  </div>
                </div>
                {study.type && (
                  <div
                    className="study-type-badge-large"
                    style={{ backgroundColor: colors.badge }}
                  >
                    {study.type}
                  </div>
                )}
              </div>

              {/* Study Content */}
              <div className="study-page-content">
                {/* Objective */}
                {hasContent(study.objective) && (
                  <div className="study-section objective-section">
                    <div
                      className="section-content"
                      dangerouslySetInnerHTML={{ __html: addPdfLinkClass(study.objective) }}
                    />
                  </div>
                )}

                {/* Image */}
                {studyImagesBase64[study.id] && (
                  <div className="study-image-section">
                    <img
                      src={studyImagesBase64[study.id]}
                      alt="Study illustration"
                      className="study-image"
                    />
                  </div>
                )}

                {/* Blurb/Summary */}
                {hasContent(study.blurb) && (
                  <div className="study-section">
                    <h4 className="section-title summary-title" style={{ color: colors.badge }}>SUMMARY OF RESULTS</h4>
                    <div
                      className="section-content"
                      dangerouslySetInnerHTML={{ __html: addPdfLinkClass(study.blurb) }}
                    />
                  </div>
                )}

                {/* Top Buttons Row - Presentation and Contact MSL */}
                <div className="study-top-buttons-row">
                  <div className="study-top-buttons-left">
                    {/* Presentation Link */}
                    {study.presentation_link && (
                      <a
                        href={study.presentation_link}
                        className="study-cta-button pdf-external-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: colors.badge }}
                      >
                        Click here for full presentation
                      </a>
                    )}
                  </div>
                  <div className="study-top-buttons-right">
                    {/* Contact Your MSL - with icon */}
                    {study.msl_contact_link && (
                      <a
                        href={study.msl_contact_link}
                        className="study-msl-button pdf-external-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundColor: colors.badge }}
                      > 
                    <svg width="23" height="23" className="msl-icon"  viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.75 21.9776V16.8411C0.75 15.2519 1.77162 13.833 3.27568 13.3505L6.3973 12.4141L11.25 17.2668L16.1027 12.4141L19.2243 13.3505C20.7284 13.8614 21.75 15.2803 21.75 16.8411V21.9776" stroke="white" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M11.2503 10.6257C13.9774 10.6257 16.1882 8.41493 16.1882 5.68784C16.1882 2.96075 13.9774 0.75 11.2503 0.75C8.52325 0.75 6.3125 2.96075 6.3125 5.68784C6.3125 8.41493 8.52325 10.6257 11.2503 10.6257Z" stroke="white" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                        Contact Your MSL
                      </a>
                    )}
                  </div>
                </div>

                <div className={`reference-col ${!(study.author_1_name || study.author_2_name) ? 'full-width' : ''}`}>
                  <div className='ref-col'>
                      {/* References */}
                      {hasContent(study.references) && (
                        <div className="study-references">
                          <p className="references-text">
                            {/* <strong>Reference:</strong>{' '} */}
                            <span dangerouslySetInnerHTML={{ __html: addPdfLinkClass(study.references) }} />
                          </p>
                        </div>
                      )}

                      {/* Abbreviations */}
                      {hasContent(study.abbreviations) && (
                        <div className="study-abbreviations">
                          <p className="abbreviations-text">
                            {/* <strong>Abbreviations:</strong>{' '} */}
                            <span dangerouslySetInnerHTML={{ __html: addPdfLinkClass(study.abbreviations) }} />
                          </p>
                        </div>
                      )}

                      {/* Study Type Legend and Prescribing Info */}
                      <div className="study-bottom-row">
                        {study.type && (
                          <div className="study-type-legend">
                            <span
                              className="legend-dot"
                              style={{ backgroundColor: colors.badge }}
                            ></span>
                            <span className="legend-text">{study.type}: {getStudyTypeName(study.type)}</span>
                          </div>
                        )}
                        {study.prescribing_link && (
                          <a
                            href={study.prescribing_link}
                            className="study-prescribing-pill pdf-external-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Prescribing Information
                          </a>
                        )}
                      </div>
                  </div>

                  {/* Affiliated Authors Section - shown when toggle is enabled and has authors */}
                  {study.show_affiliated_authors !== false && (study.author_1_name || study.author_2_name) && (
                    <div className="study-affiliated-authors">
                      <p className="affiliated-authors-title">
                        {study.affiliated_authors_title || 'Retina Consultants of America (RCA) affiliated author:'}
                      </p>
                      <div className="affiliated-authors-content">
                        <div className="affiliated-author-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.2599 18.5381L10.4647 18.9068C10.8744 18.7634 11.1816 18.3537 11.0382 18.026L10.055 15.9981C9.74776 15.3836 9.09229 15.0558 8.25246 15.3631C7.43311 15.6499 7.12586 16.3258 7.24876 17.0018L7.76085 19.1935C7.86327 19.5418 8.37536 19.6647 8.76455 19.5213L8.62116 19.1321" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M14.0078 16.734C14.0078 17.3895 14.5404 17.9015 15.1754 17.9015C15.8104 17.9015 16.3429 17.369 16.3429 16.734C16.3429 16.099 15.8104 15.5664 15.1754 15.5664C14.5404 15.5664 14.0078 16.099 14.0078 16.734Z" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M4.42188 20.1564L4.72913 16.0597C4.87252 14.7078 7.00281 13.4173 9.54279 12.8438L11.3863 14.7487C11.7345 15.1174 12.2671 15.1174 12.6153 14.7487L14.4589 12.8438C16.9783 13.4173 19.1086 14.6873 19.2725 16.0597L19.5798 20.1564" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M15.5234 15.6097C15.8921 14.7289 16.0355 14.1963 16.0765 13.418" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M7.80078 13.457C7.82126 14.174 7.96465 14.7885 8.23094 15.362" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M12.1428 11.6138C14.3036 11.6138 16.0552 9.86218 16.0552 7.70144C16.0552 5.54069 14.3036 3.78906 12.1428 3.78906C9.9821 3.78906 8.23047 5.54069 8.23047 7.70144C8.23047 9.86218 9.9821 11.6138 12.1428 11.6138Z" stroke="#7F7F7F" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <circle cx="12" cy="12" r="11.4" stroke="#7F7F7F" stroke-width="1.2"/>
                          </svg>
                        </div>
                        <div className="affiliated-authors-names">
                          {study.author_1_name && (
                            <span className="affiliated-author-name">{study.author_1_name}</span>
                          )}
                          {study.author_2_name && (
                            <span className="affiliated-author-name">{study.author_2_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Spacer to push footer to bottom */}
              {/* <div className="page-content-spacer"></div> */}

              {renderPageFooter(index + 2, totalPages)}
            </div>
          );
        })}
      </>
    );
  };

  // Render default empty page with header and footer structure (when no studies exist)
  const renderEmptyPage = () => (
    <div className="preview-page empty-page visible">
      {renderPageHeader()}

      {/* Disclaimers Section - shown even without studies */}
      {hasContent(guide.disclaimers) && (
          <div
            className="study-disclaimers"
            dangerouslySetInnerHTML={{
              __html: addPdfLinkClass(stripOuterPTags(guide.disclaimers)),
            }}
          />
        )}

      {/* Empty content area with placeholder */}
      <div className="empty-page-content">
        <div className="empty-content-placeholder">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 20H32M16 28H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p>Add studies to see them here</p>
        </div>
      </div>

      {/* Authors Panel - shown when content exists (title or authors) */}
      {(guide.authors?.length > 0 || guide.author_panel_title) && (
        <div className="home-authors-panel">
          {guide.author_panel_title && (
            <h4 className="authors-panel-title">{guide.author_panel_title}</h4>
          )}
          {guide.authors && guide.authors.length > 0 && (
            <div className="authors-list-display">
              <div className="author-icon">
                <svg width="43" height="49" viewBox="0 0 43 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.3988 25.4986C15.1338 25.4986 10.0234 19.7664 10.0234 12.7493C10.0234 5.73225 15.1092 0 21.3988 0C27.6885 0 32.7497 5.73225 32.7497 12.7493C32.7497 19.7664 27.6639 25.4986 21.3988 25.4986ZM21.3988 0.988319C15.6743 0.988319 11.0308 6.27582 11.0308 12.7493C11.0308 19.2228 15.6743 24.5103 21.3988 24.5103C27.1234 24.5103 31.7669 19.2228 31.7669 12.7493C31.7669 6.27582 27.1234 0.988319 21.3988 0.988319Z" fill="#B3CEFF"/>
                <path d="M31.7667 9.53826H26.73C24.9119 9.53826 23.2167 8.82173 21.9391 7.53692C20.8581 6.44977 19.3839 5.83207 17.8361 5.83207H12.7258C12.4555 5.83207 12.2344 5.6097 12.2344 5.33791C12.2344 5.06612 12.4555 4.84375 12.7258 4.84375H17.8361C19.6542 4.84375 21.3494 5.56028 22.627 6.8451C23.7081 7.93225 25.1822 8.54995 26.73 8.54995H31.7667C32.0369 8.54995 32.258 8.77232 32.258 9.0441C32.258 9.31589 32.0369 9.53826 31.7667 9.53826Z" fill="#B3CEFF"/>
                <path d="M21.3993 37.4813C21.2273 37.4813 21.0799 37.4072 20.9816 37.2342L15.6011 28.537C15.5519 28.4629 15.5273 28.3641 15.5273 28.2652V23.3731C15.5273 23.1013 15.7485 22.8789 16.0187 22.8789C16.289 22.8789 16.5101 23.1013 16.5101 23.3731V28.117L21.817 36.6907C21.9644 36.913 21.8907 37.2342 21.6696 37.3825C21.5959 37.4319 21.4976 37.4566 21.3993 37.4566V37.4813Z" fill="#B3CEFF"/>
                <path d="M36.1654 48.8707H3.6362C1.62155 48.8707 0 47.2153 0 45.2139V40.3959C0 37.7521 1.44957 35.3554 3.78361 34.1447L15.8224 27.8442C16.0681 27.7207 16.3629 27.8195 16.4857 28.0666C16.6086 28.3137 16.5103 28.6101 16.2646 28.7337L4.22585 35.0342C2.23577 36.072 0.982756 38.1474 0.982756 40.3959V45.2139C0.982756 46.6964 2.16206 47.8824 3.6362 47.8824H36.1654C36.4357 47.8824 36.6568 48.1047 36.6568 48.3765C36.6568 48.6483 36.4357 48.8707 36.1654 48.8707Z" fill="#B3CEFF"/>
                <path d="M21.4006 37.482C21.3023 37.482 21.2286 37.482 21.1304 37.4079C20.9092 37.2596 20.8355 36.9631 20.9829 36.7161L26.2898 28.1424V23.3985C26.2898 23.1267 26.5109 22.9043 26.7812 22.9043C27.0515 22.9043 27.2726 23.1267 27.2726 23.3985V28.2906C27.2726 28.3895 27.248 28.4636 27.1989 28.5624L21.8183 37.2596C21.72 37.4079 21.5726 37.5067 21.4006 37.5067V37.482Z" fill="#B3CEFF"/>
                <path d="M39.1641 48.8707H36.1667C35.8965 48.8707 35.6754 48.6483 35.6754 48.3765C35.6754 48.1047 35.8965 47.8824 36.1667 47.8824H39.1641C40.6383 47.8824 41.8176 46.6964 41.8176 45.2139V40.3959C41.8176 38.1227 40.5646 36.072 38.5745 35.0342L26.5357 28.7337C26.29 28.6101 26.1918 28.3137 26.3146 28.0666C26.4374 27.8195 26.7323 27.7207 26.978 27.8442L39.0167 34.1447C41.3508 35.3554 42.8003 37.7521 42.8003 40.3959V45.2139C42.8003 47.24 41.1542 48.8707 39.1641 48.8707Z" fill="#B3CEFF"/>
                <path d="M7.36898 43.4358C7.2707 43.4358 7.17242 43.4111 7.07415 43.337C6.97587 43.2629 6.90217 43.1393 6.8776 43.0158L6.26338 39.0625C6.11596 38.173 6.33708 37.3082 6.8776 36.567C7.39354 35.8505 8.17975 35.381 9.06423 35.2328C10.8578 34.961 12.5776 36.1964 12.8724 38.0248L13.4866 41.978C13.5358 42.2498 13.3392 42.4969 13.069 42.5463L11.6685 42.7687C11.3983 42.8181 11.1526 42.6204 11.1034 42.3487C11.0543 42.0769 11.2509 41.8298 11.5211 41.7804L12.4302 41.6321L11.8897 38.173C11.6931 36.8882 10.4892 36.0234 9.23621 36.2211C8.62199 36.3199 8.08147 36.6411 7.71294 37.16C7.34441 37.6541 7.19699 38.2966 7.29527 38.9143L7.83579 42.3734L8.74483 42.2251C9.01509 42.1757 9.26078 42.3734 9.30992 42.6452C9.35906 42.9169 9.16251 43.164 8.89225 43.2134L7.49182 43.4358C7.46725 43.4358 7.44268 43.4358 7.41811 43.4358H7.36898Z" fill="#B3CEFF"/>
                <path d="M9.13907 36.2461C8.89338 36.2461 8.67226 36.0732 8.64769 35.8014L8.20545 32.4411C8.18088 32.1693 8.35287 31.9222 8.62312 31.8728C8.89338 31.8234 9.13907 32.0211 9.18821 32.2929L9.63045 35.6531C9.65502 35.9249 9.48304 36.172 9.21278 36.2214C9.21278 36.2214 9.16364 36.2214 9.13907 36.2214V36.2461Z" fill="#B3CEFF"/>
                <path d="M31.5702 43.0909C30.8577 43.0909 30.1452 42.8685 29.531 42.4238C28.7694 41.8802 28.278 41.0649 28.1306 40.1507C27.9831 39.2365 28.2043 38.2976 28.7448 37.5563C29.875 35.9997 32.037 35.6538 33.5849 36.7657C35.1327 37.9022 35.4767 40.0765 34.3711 41.6331C33.6831 42.572 32.6267 43.0662 31.5456 43.0662L31.5702 43.0909ZM31.5702 37.1116C30.8086 37.1116 30.0469 37.4575 29.5556 38.1493C29.1625 38.6929 29.015 39.36 29.1133 40.0024C29.2116 40.6695 29.5801 41.2378 30.0961 41.6331C31.2017 42.4485 32.7495 42.2014 33.5603 41.0896C34.3711 39.9777 34.1254 38.4211 32.9952 37.6057C32.553 37.2845 32.037 37.1363 31.5456 37.1363L31.5702 37.1116Z" fill="#B3CEFF"/>
                <path d="M32.3568 37.2355C32.3077 37.2355 32.2585 37.2355 32.234 37.2355C31.9637 37.1614 31.8163 36.8896 31.89 36.6178L33.0939 32.2692C33.1676 31.9974 33.4378 31.8492 33.7081 31.9233C33.9784 31.9974 34.1258 32.2692 34.0521 32.541L32.8482 36.8896C32.7991 37.112 32.5779 37.2602 32.3814 37.2602L32.3568 37.2355Z" fill="#B3CEFF"/>
                </svg>
              </div>
              <div className="authors-text-list">
                {guide.authors.map((author, index) => (
                  <div key={index} className="author-display-item">
                    <div className="author-info">
                      {author.name && <span className="author-display-name">{author.name}</span>}
                      {author.study_title && <span className="author-display-study">{author.study_title}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {renderPageFooter(1, 1)}
    </div>
  );

  return (
    <div className={`live-preview ${theme} ${isExporting ? 'exporting' : ''}`}>
      {studies.length > 0 ? (
        renderAllPages()
      ) : (
        renderEmptyPage()
      )}
    </div>
  );
};

export default LivePreview;
